import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { OutstandingPayablesWidget } from '@/components/dashboard/OutstandingPayablesWidget';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useVisibility } from '@/hooks/useVisibility';
import { resetDashboardTour } from '@/components/DashboardTour';
import { useBranchStore, useAuthStore } from '@/stores';
// Data widgets — surfaced inline from this page
import DunningScorecardWidget from '@/pages/dashboard/widgets/DunningScorecardWidget';
import StockAlertTrendsWidget from '@/pages/dashboard/widgets/StockAlertTrendsWidget';
import RecentTransactionsWidget from '@/pages/dashboard/widgets/RecentTransactionsWidget';
import QuickChartsWidget from '@/pages/dashboard/widgets/QuickChartsWidget';
import QuickActionsWidget from '@/pages/dashboard/widgets/QuickActionsWidget';
import SpendingWidget from '@/pages/dashboard/widgets/SpendingWidget';
import SupplierDuesWidget from '@/pages/dashboard/widgets/SupplierDuesWidget';
import BankAccountSummaryWidget from '@/pages/dashboard/widgets/BankAccountSummaryWidget';
import AnomalyDetectionWidget from '@/pages/dashboard/widgets/AnomalyDetectionWidget';
import AIPredictiveWidgets from '@/pages/dashboard/widgets/AIPredictiveWidgets';
import InterBranchDueWidget from '@/pages/dashboard/widgets/InterBranchDueWidget';
import BranchTilesWidget from '@/pages/dashboard/widgets/BranchTilesWidget';
// Page chrome — CTAs, banners, sections, modals
import DashboardSampleDataCTA from '@/components/dashboard/DashboardSampleDataCTA';
import DashboardSetupScore from '@/components/dashboard/DashboardSetupScore';
import { LaunchHealthBadge } from '@/components/dashboard/LaunchHealthBadge';
import DashboardBookmarks from '@/components/dashboard/DashboardBookmarks';
import DashboardSmartWidgets from '@/components/dashboard/DashboardSmartWidgets';
import DashboardAlertsCards from '@/components/dashboard/DashboardAlertsCards';
import TailorPickupsCard from '@/components/dashboard/TailorPickupsCard';
import DashboardSection from '@/components/dashboard/DashboardSection';
import { CompareModeToolbar } from '@/components/dashboard/CompareModeToolbar';
import { ReconciliationNudge } from '@/components/dashboard/ReconciliationNudge';
import PendingOffsetsAlertCard from '@/components/dashboard/PendingOffsetsAlertCard';
import UnassignedRevenueChip from '@/components/dashboard/UnassignedRevenueChip';
import { ZatcaStatusDot } from '@/components/pos/ZatcaStatusDot';
import DashboardHealthStrip from '@/components/dashboard/DashboardHealthStrip';
import { RecurringDigestBanner } from '@/components/dashboard/RecurringDigestBanner';
import { EditModeBanner } from '@/components/dashboard/EditModeBanner';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { DashboardFilterBar } from '@/components/dashboard/DashboardFilterBar';
import { VatModeToggle } from '@/components/common/VatModeToggle';
import { useVatModeStore } from '@/stores/vatModeStore';
import { StatusStripSection } from '@/components/dashboard/StatusStripSection';
import { OverviewSection } from '@/components/dashboard/OverviewSection';
import { MoneyMovementSection } from '@/components/dashboard/MoneyMovementSection';
import { AlertsSection } from '@/components/dashboard/AlertsSection';
import { DashboardModalsHost } from '@/components/dashboard/DashboardModalsHost';
import VATSummaryCards from '@/components/dashboard/VATSummaryCards';
import { FreshnessTag } from '@/components/common/FreshnessTag';
import { VATLiabilityWidget } from '@/components/accounting/VATLiabilityWidget';
// iter418 — ZATCA CSID expiry banner, self-mounts when expiry < alert_days.
import ZatcaCsidBanner from '@/components/banking/ZatcaCsidBanner';
import ZatcaHealthWidget from '@/components/dashboard/ZatcaHealthWidget';
// iter687 — First-Run Country Setup banner — auto-hides when the
// tenant has already completed setup. Banner owns its own wizard
// mount so the wizard isn't loaded eagerly on every dashboard load.
import DashboardSetupCountryBanner from '@/components/dashboard/DashboardSetupCountryBanner';
// Constants + hooks
import { THEMES } from '@/components/dashboard/dashboardConstants';
import { QUICK_ACTIONS } from '@/components/dashboard/dashboardLayoutHelpers';
import { useDashboardRealtimeToasts } from '@/hooks/useDashboardRealtimeToasts';
import useCompareMode from '@/hooks/useCompareMode';
import useDashboardLayout from '@/hooks/useDashboardLayout';
import useDashboardStats from '@/hooks/useDashboardStats';
import { buildStatCards, buildPaymentModeCards } from '@/components/dashboard/dashboardCards';


export default function DashboardPage() {
  // Zustand stores
  const { branches, fetchBranches } = useBranchStore();
  const { user } = useAuthStore();
  const vis = useVisibility();
  const userModules = user?.allowed_modules || [];
  const hasModule = (mod) => !userModules.length || userModules.includes(mod) || user?.role === 'admin';
  const showWidget = (key) => {
    if (!userModules.length || user?.role === 'admin') return true;
    const widgetModules = {
      revenue: ['pos','sales','accounting'], expenses: ['accounting','expenses'], profit: ['accounting'],
      cash_bank: ['accounting'], branch_sales: ['pos','sales'], branch_pnl: ['accounting'],
      employees: ['hr'], attendance: ['hr'], payroll: ['hr'], hr_alerts: ['hr'],
      supplier: ['accounting','expenses'], production: ['factory'],
    };
    const required = widgetModules[key] || [];
    return !required.length || required.some(m => userModules.includes(m));
  };

  // Local UI state (chrome only — data moves into useDashboardStats below)
  const [branchFilter, setBranchFilter] = useState([]);
  const inclVat = useVatModeStore((s) => s.inclVat);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: 'all' });
  const [showVat, setShowVat] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('dashboard_theme') || 'default');
  const [showPaybackDialog, setShowPaybackDialog] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDuesDetail, setShowDuesDetail] = useState(false);
  const [duesDetailData, setDuesDetailData] = useState([]);
  const [showTour, setShowTour] = useState(false);
  const t = THEMES[theme] || THEMES.default;
  const { t: tr, language } = useLanguage();

  // User permissions from Zustand
  const userPermissions = user?.permissions || {};
  const hasPermission = (perm) => {
    if (!perm) return true;
    const level = userPermissions[perm];
    return level === 'read' || level === 'write';
  };

  // iter417 — Compare-mode + Layout/widget hooks
  const {
    comparePeriod, setComparePeriod,
    compareEnabled, setCompareEnabled,
    compareData, compareLoading,
  } = useCompareMode({ branchFilter });
  const {
    widgets, setWidgets,
    layout, // eslint-disable-line no-unused-vars
    toggleWidget,
    handleLayoutChange, // eslint-disable-line no-unused-vars
    resetLayout,
  } = useDashboardLayout({ tr });

  // iter453 — All dashboard data (stats / alerts / dues / predictions /
  // anomaly / budget / stock-trends / dunning / recent-txns) now lives in
  // a single hook so this file stays focused on layout.
  const {
    stats, alerts, branchDues, pendingSalaries, todayVsYest, dailyTrend,
    predictiveData, missingDataAlerts, bankAccountSummary, anomalyHistory,
    budgetAlertsSummary, stockAlertTrends, dunningScore, recentTransactions,
    loading, fetchStats, fetchStatsRef, refreshBudgetAlerts, loadGroup,
  } = useDashboardStats({ branchFilter, dateFilter, hasPermission });

  // Real-time WebSocket connection
  const { isConnected: wsConnected } = useDashboardRealtimeToasts({
    onStatsRefresh: () => fetchStatsRef.current && fetchStatsRef.current(),
    onBudgetAlert: refreshBudgetAlerts,
  });

  // Branches are loaded separately (Zustand store) — refetch on filter change.
  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const handleViewDuesDetail = async () => {
    try {
      const res = await api.get('/reports/branch-dues-detail');
      setDuesDetailData(res.data?.entries || []);
      setShowDuesDetail(true);
    } catch { toast.error('Failed to load dues detail'); }
  };

  // Show tour on mount only if explicitly enabled.
  useEffect(() => {
    const tourEnabled = localStorage.getItem('ssc_dashboard_tour_enabled');
    const tourCompleted = localStorage.getItem('ssc_dashboard_tour_completed');
    if (tourEnabled === 'true' && !tourCompleted) {
      const timer = setTimeout(() => setShowTour(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTourComplete = () => {
    setShowTour(false);
    toast.success(tr('tour_completed') || 'Tour completed! Explore your dashboard.');
  };

  const startTour = () => {
    resetDashboardTour();
    localStorage.setItem('ssc_dashboard_tour_enabled', 'true');
    setShowTour(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">Loading...</div>
      </DashboardLayout>
    );
  }

  const fmtAmt = (v) => (v ?? 0).toFixed(2);
  // iter453 — pure builders moved to dashboardCards.js
  const statCards = buildStatCards({ stats, vis, tr, dailyTrend, dateFilter, inclVat });
  const paymentModeCards = buildPaymentModeCards({ stats });

  return (
    <DashboardLayout>
      <div className={`space-y-6 min-h-screen p-1 rounded-2xl ${theme === 'dark' ? 'bg-gradient-to-br ' + t.bg + ' text-white' : ''}`}>
        {/* iter951 — network-wide reconciliation health strip (very top) */}
        <DashboardHealthStrip />
        <div className="flex justify-end -mt-2 items-center gap-3"><ZatcaStatusDot showLabel /><LaunchHealthBadge /></div>
        {/* Edit Mode Banner — extracted in iter414 */}
        {isEditMode && <EditModeBanner onExit={() => setIsEditMode(false)} />}
        <DashboardHeader
          themeAccentClass={t.accent}
          themeMutedClass={theme === 'dark' ? 'text-muted-foreground' : 'text-muted-foreground'}
          wsConnected={wsConnected}
          isEditMode={isEditMode}
          onToggleEdit={() => setIsEditMode(!isEditMode)}
          onReset={resetLayout}
          onOpenWhatsApp={() => setShowWhatsApp(true)}
          onOpenWidgetSettings={() => setShowWidgetSettings(true)}
          onStartTour={startTour}
          tr={tr}
        />
        <div className="flex justify-end"><VatModeToggle /></div>
        <DashboardFilterBar
          branchFilter={branchFilter}
          dateFilter={dateFilter}
          compareEnabled={compareEnabled}
          onBranchChange={setBranchFilter}
          onDateChange={setDateFilter}
        />

        <DashboardSampleDataCTA />
        <DashboardSetupScore />
        {/* iter687 — Country Setup Wizard banner. Self-hides once
            tenant.setup_complete === true. Drives the SSoT data flow. */}
        <DashboardSetupCountryBanner />
        <DashboardBookmarks />

        {/* iter418 — ZATCA CSID expiry banner. Self-hides when status is
            healthy or Phase 2 is disabled — never adds dashboard noise
            on KSA-compliant tenants. */}
        <ZatcaCsidBanner testidPrefix="dashboard-csid-banner" />

        {/* iter805 — ZATCA compliance health (today's reported/failed +
            bulk retry). Self-hides for non-onboarded tenants. */}
        <ZatcaHealthWidget />

        {/* Foodics-style Period Compare Toggle */}
        <CompareModeToolbar
          comparePeriod={comparePeriod}
          setComparePeriod={setComparePeriod}
          compareEnabled={compareEnabled}
          setCompareEnabled={setCompareEnabled}
          compareData={compareData}
          compareLoading={compareLoading}
        />

        {/* Onboarding nudge — only renders when reconciliation is genuinely idle */}
        <ReconciliationNudge />
        <PendingOffsetsAlertCard />
        <div className="flex flex-wrap gap-2"><UnassignedRevenueChip /></div>
        <RecurringDigestBanner />

        {/* ── OVERVIEW — KPIs first; Status strip moved below Money Movement ── */}
        {widgets.stats && (
          <>
          <div className="flex justify-end -mb-2"><FreshnessTag fetchedAt={stats?.generated_at} testId="dashboard-freshness" /></div>
          <OverviewSection
            statCards={statCards}
            todayVsYest={todayVsYest}
            fmtAmt={fmtAmt}
            hasModule={hasModule}
          />
          </>
        )}

        {/* ── MONEY MOVEMENT — extracted in iter416 ─────────────────────── */}
        <MoneyMovementSection
          stats={stats}
          branches={branches}
          paymentModeCards={paymentModeCards}
          fmtAmt={fmtAmt}
          widgets={widgets}
          showWidget={showWidget}
          onSettled={fetchStats}
        />

        {widgets.stats && <StatusStripSection />}

        {/* ── BRANCH TILES — per-branch owed/owe chip + nightly auto-net ─── */}
        {widgets.branchTiles !== false && (
          <BranchTilesWidget branches={branches} />
        )}

        {/* ── ALERTS — extracted in iter417 ──────────────────────────────── */}
        <AlertsSection
          stats={stats}
          missingDataAlerts={missingDataAlerts}
          budgetAlertsSummary={budgetAlertsSummary}
          widgets={widgets}
          showWidget={showWidget}
          cardClass={t.card}
        />

        {/* ── ACTIONS — quick entry + activity digest ─────────────────────── */}
        {(widgets.quickActions || widgets.recentTransactions || widgets.charts) && (
          <DashboardSection title="Actions & Activity" testId="dash-section-actions">
            {widgets.quickActions && (
              <QuickActionsWidget actions={QUICK_ACTIONS} hasPermission={hasPermission} language={language} tr={tr} />
            )}
            <DashboardSmartWidgets />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
              {widgets.recentTransactions && (
                <RecentTransactionsWidget recentTransactions={recentTransactions} cardClass={t.card} />
              )}
              {widgets.charts && (
                <QuickChartsWidget stats={stats} cardClass={t.card} />
              )}
            </div>
          </DashboardSection>
        )}

        {/* ── INSIGHTS — spending, dues, bank, anomaly, VAT ───────────────── */}
        <DashboardSection title="Insights" subtitle="spending, dues, bank, VAT" testId="dash-section-insights" collapsible defaultOpen={false} onOpen={() => loadGroup('insights')}>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            {widgets.spending && (
              <SpendingWidget stats={stats} />
            )}
            {widgets.dues && (
              <SupplierDuesWidget
                stats={stats}
                pendingSalaries={pendingSalaries}
                branchDues={branchDues}
                onViewDues={handleViewDuesDetail}
                onRecordPayback={() => setShowPaybackDialog(true)}
              />
            )}
            <BankAccountSummaryWidget bankAccountSummary={bankAccountSummary} />
            <AnomalyDetectionWidget anomalyHistory={anomalyHistory} />
            {/* iter839 — Inter-Branch Due summary + Close Month */}
            <InterBranchDueWidget />
          </div>
          {widgets.vatSummary && (
            <VATSummaryCards stats={stats} showVat={showVat} onToggleVat={setShowVat} />
          )}
          {widgets.vatLiability !== false && (
            <div data-testid="dash-vat-liability-card">
              <VATLiabilityWidget compact />
            </div>
          )}
          <OutstandingPayablesWidget currency={stats?.currency || 'SAR'} />
        </DashboardSection>

        {/* ── PREDICTIONS — AI-driven forecasts and scorecards ────────────── */}
        <DashboardSection title="Predictions & Scorecards" testId="dash-section-predictions" collapsible defaultOpen={false} onOpen={() => loadGroup('predictions')}>
          <AIPredictiveWidgets predictiveData={predictiveData} widgets={widgets} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
            {widgets.stockAlertTrends && (
              <StockAlertTrendsWidget stockAlertTrends={stockAlertTrends} />
            )}
            {widgets.dunningScorecard && (
              <DunningScorecardWidget dunningScore={dunningScore} />
            )}
          </div>
        </DashboardSection>

        {/* ── UPCOMING — recurring expenses + document expiry ─────────────── */}
        <DashboardSection title="Upcoming" subtitle="recurring expenses, documents" testId="dash-section-upcoming" collapsible defaultOpen={false} onOpen={() => loadGroup('upcoming')}>
          {hasModule('tailor') && <TailorPickupsCard />}
          <DashboardAlertsCards
            upcomingExpenses={stats?.upcoming_expenses || []}
            documentAlerts={alerts}
          />
        </DashboardSection>

        {/* iter417 — All modal mounts consolidated into one host */}
        <DashboardModalsHost
          showPayback={showPaybackDialog}
          setShowPayback={setShowPaybackDialog}
          branches={branches}
          onPaybackSuccess={fetchStats}
          showWhatsApp={showWhatsApp}
          setShowWhatsApp={setShowWhatsApp}
          showWidgetSettings={showWidgetSettings}
          setShowWidgetSettings={setShowWidgetSettings}
          widgets={widgets}
          setWidgets={setWidgets}
          toggleWidget={toggleWidget}
          tr={tr}
          showTour={showTour}
          onTourComplete={handleTourComplete}
          language={language}
          showDuesDetail={showDuesDetail}
          setShowDuesDetail={setShowDuesDetail}
          duesDetailData={duesDetailData}
        />
      </div>
    </DashboardLayout>
  );
}
